import React, { useState, useEffect, useRef } from "react";
import Modal from "./Modal";
import { Building, CheckCheck, Copy, Minus, Plus, StickyNote } from "lucide-react";
import { Person, Person2, PersonOutline, PersonOutlineOutlined, PersonPinCircle } from "@mui/icons-material";
import { Checkbox, Contact, Dropdown, MultiFile, Text } from "./FieldComponents";
import { ContactFieldPresets } from "./ContactFieldPresets";

export const CreateContact = ({ setCreateContact, fetchContacts, onContactCreated, opportunity }) => {
    const [contactInformation, setContactInformation] = useState({
        first_name: '',
        middle_name: '',
        last_name: '',
        full_name: '',
        nickname: '',
        prefix: '',
        suffix: '',
        company_name: '',
        job_title: '',
        department: '',
        date_of_birth: '',
        date_of_death: '',
        is_company: false,
        contact_type: '',
        gender: '',
        pronouns: '',
        language: '',
        emergencyContact: null,
        socialMedia: [],
        ssn: '',
        dl: '',
        dlp: [],
        education: '',
        additional_notes: '',
        opportunity: opportunity || false
    });
    const [contactTypes, setContactTypes] = useState([]);
    const [currentPage, setCurrentPage] = useState(0);
    const [copiedAddressIndex, setCopiedAddressIndex] = useState(null);
    const [showNoteFor, setShowNoteFor] = useState(null); // 'phone-0', 'email-1', 'address-2', etc.
    const noteTextareaRef = useRef(null);
    const [contactPresets, setContactPresets] = useState([]);
    const [presetFieldMap, setPresetFieldMap] = useState({});
    
    const [profilePicture, setProfilePicture] = useState(null);
    const [profilePicturePreviewUrl, setProfilePicturePreviewUrl] = useState(null);
    
    const [phones, setPhones] = useState([{ type: "mobile", number: "", note: "" }]);
    const [emails, setEmails] = useState([{ type: "personal", email: "", note: "" }]);
    const [addresses, setAddresses] = useState([{
        type: "home",
        line1: "",
        line2: "",
        city: "",
        state: "",
        postal_code: "",
        note: ""
    }]);
    
    const addPhone = () => setPhones([...phones, { type: "mobile", number: "", note: "" }]);
    const addEmail = () => setEmails([...emails, { type: "personal", email: "", note: "" }]);
    const addAddress = () => setAddresses([...addresses, {
        type: "home", line1: "", line2: "", city: "", state: "", postal_code: "", note: ""
    }]);

    useEffect(() => {
        if (!profilePicture) {
            setProfilePicturePreviewUrl(null);
            return;
        }

        const nextPreviewUrl = URL.createObjectURL(profilePicture);
        setProfilePicturePreviewUrl(nextPreviewUrl);

        return () => {
            URL.revokeObjectURL(nextPreviewUrl);
        };
    }, [profilePicture]);
    
    const formatAge = (birthDate) => {
        if (!birthDate) return '';
        
        const birth = new Date(birthDate);
        const today = new Date();
        
        let years = today.getFullYear() - birth.getFullYear();
        let months = today.getMonth() - birth.getMonth();
        let days = today.getDate() - birth.getDate();
        
        // Adjust for negative days
        if (days < 0) {
            months--;
            const lastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
            days += lastMonth.getDate();
        }
        
        // Adjust for negative months
        if (months < 0) {
            years--;
            months += 12;
        }
        
        const parts = [];
        if (years > 0) parts.push(`${years} year${years !== 1 ? 's' : ''}`);
        if (months > 0) parts.push(`${months} month${months !== 1 ? 's' : ''}`);
        if (days > 0 && years === 0) parts.push(`${days} day${days !== 1 ? 's' : ''}`); // Only show days if less than a year
        
        return parts.length > 0 ? ` (${parts.join(', ')} old)` : '';
    };
    
    const handleProfilePictureChange = (e) => {
        setProfilePicture(e.target.files[0]);
    };

    const formatFullAddress = (address) => {
        const parts = [];
        
        if (address.line1) parts.push(address.line1);
        if (address.line2) parts.push(address.line2);
        if (address.city) parts.push(address.city);
        
        let stateZip = '';
        if (address.state && address.postal_code) {
            stateZip = `${address.state} ${address.postal_code}`;
        } else if (address.state) {
            stateZip = address.state;
        } else if (address.postal_code) {
            stateZip = address.postal_code;
        }
        
        if (stateZip) parts.push(stateZip);
        
        return parts.join(', ');
    };

    const copyAddressToClipboard = async (address, index) => {
        const fullAddress = formatFullAddress(address);
        if (!fullAddress) return;
        
        try {
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(fullAddress);
            } else {
                // Fallback for older browsers or non-secure contexts
                const textArea = document.createElement('textarea');
                textArea.value = fullAddress;
                textArea.style.position = 'fixed';
                textArea.style.opacity = '0';
                document.body.appendChild(textArea);
                textArea.focus();
                textArea.select();
                document.execCommand('copy');
                document.body.removeChild(textArea);
            }
            
            // Show copied feedback
            setCopiedAddressIndex(index);
            setTimeout(() => setCopiedAddressIndex(null), 3000);
        } catch (err) {
            console.error('Failed to copy address: ', err);
        }
    };
    
    const createNewContact = async () => {
        const formData = new FormData();
    
        // Helper function to check if a value should be included
        const hasValue = (value) => {
            if (value === null || value === undefined || value === '') return false;
            if (typeof value === 'string' && value.trim() === '') return false;
            return true;
        };

        // Helper function to check if an address object has any meaningful data
        const hasAddressData = (address) => {
            return hasValue(address.line1) || hasValue(address.line2) || 
                   hasValue(address.city) || hasValue(address.state) || 
                   hasValue(address.postal_code);
        };

        // Helper function to check if a phone object has meaningful data
        const hasPhoneData = (phone) => {
            return hasValue(phone.number);
        };

        // Helper function to check if an email object has meaningful data
        const hasEmailData = (email) => {
            return hasValue(email.email);
        };

        // Only append contact information fields that have values
        Object.keys(contactInformation).forEach(key => {
            const value = contactInformation[key];
            if (hasValue(value)) {
                formData.append(key, value);
            }
        });
    
        formData.set('is_company', contactInformation.is_company ? 'true' : 'false');

        // Add personal details separately to ensure proper data handling
        formData.append('gender', contactInformation.gender || '');
        formData.append('pronouns', contactInformation.pronouns || '');
        formData.append('language', contactInformation.language || '');
        formData.append('emergencyContact', contactInformation.emergencyContact || '');
        formData.append('socialMedia', JSON.stringify(contactInformation.socialMedia || []));
        formData.append('ssn', contactInformation.ssn || '');
        formData.append('dl', contactInformation.dl || '');
        formData.append('dlp', JSON.stringify(contactInformation.dlp || []));
        formData.append('education', contactInformation.education || '');
        formData.append('additional_notes', contactInformation.additional_notes || '');
    
        // Filter phones to only include those with actual phone numbers
        const filteredPhones = phones.filter(hasPhoneData);
        if (filteredPhones.length > 0) {
            formData.append('phones', JSON.stringify(filteredPhones));
        }

        // Filter emails to only include those with actual email addresses
        const filteredEmails = emails.filter(hasEmailData);
        if (filteredEmails.length > 0) {
            formData.append('emails', JSON.stringify(filteredEmails));
        }

        // Filter addresses to only include those with actual address data
        const filteredAddresses = addresses.filter(hasAddressData);
        if (filteredAddresses.length > 0) {
            formData.append('addresses', JSON.stringify(filteredAddresses));
        }
    
        if (profilePicture) {
            formData.append('profile_picture', profilePicture);
        }

        const presetPayload = contactPresets
            .filter(preset => preset.source_field_id && preset.target_field_id)
            .map((preset) => ({
                source_field_id: preset.source_field_id,
                target_field_id: preset.target_field_id,
                target_value: preset.target_value
            }));
        formData.append('contact_field_presets', JSON.stringify(presetPayload));
    
        try {
            const response = await fetch(`https://api.casedb.co/contacts.php`, {
                method: "POST",
                body: formData,
                credentials: 'include'
            });
    
            if (response.ok) {
                const data = await response.json();
                
                if (data.success && data.contact_id) {
                    // Create contact object with the data we have
                    const newContact = {
                        id: data.contact_id,
                        contact_id: data.contact_id,
                        full_name: contactInformation.is_company ? contactInformation.company_name : 
                                  `${contactInformation.first_name || ''} ${contactInformation.last_name || ''}`.trim(),
                        first_name: contactInformation.first_name,
                        last_name: contactInformation.last_name,
                        company_name: contactInformation.company_name,
                        is_company: contactInformation.is_company,
                        contact_type: contactInformation.contact_type,
                        phones: phones,
                        emails: emails,
                        addresses: addresses,
                        profile_picture: profilePicture ? data.profile_picture_path : null,
                        ...contactInformation
                    };
                    
                    // Call the callback with the new contact if provided
                    if (onContactCreated && typeof onContactCreated === 'function') {
                        onContactCreated(newContact);
                    }
                }
                
                if (fetchContacts && typeof fetchContacts === 'function') {
                    fetchContacts();
                }
                setCreateContact(false);
            } else {
                console.error("Error creating contact:", response.statusText);
            }
        } catch (error) {
            console.error("Error creating contact:", error);
        }
    };

    useEffect(() => {
        const response = fetch("https://api.casedb.co/contacts.php?types=true", { credentials: 'include' });
        response.then(res => res.json()).then(data => {
            if (data.success) {
                setContactTypes(data.contact_types || []);
            } else {
                console.error("Error fetching contact types:", data.message);
            }
        }).catch(error => {
            console.error("Error fetching contact types:", error);
        });
    }, []);

    // Focus textarea when note section is opened
    useEffect(() => {
        if (showNoteFor && noteTextareaRef.current) {
            noteTextareaRef.current.focus();
        }
    }, [showNoteFor]);

    return (
        <Modal
            onClose={() => setCreateContact(false)}
            title="Create New Contact"
            wider
            header={(
                <div className='modal-header-actions'>
                    <button className='action alt' onClick={() => setCreateContact(false)}>Cancel</button>
                    <button className='action' onClick={() => {
                        createNewContact();
                        setCreateContact(false);
                    }}>Save</button>
                </div>
            )}
        >
            <div className='modal-content-wrapper'>
                <form style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', flexDirection: 'column', gap: '20px', width: `calc(100% - 30px)` }}>
                    <div className='modal-navigation'>
                        <span 
                            className={`${currentPage === 0 ? 'active' : ''}`} 
                            onClick={() => setCurrentPage(0)}
                        >
                            Basic Information
                        </span>
                        <span 
                            className={`${currentPage === 1 ? 'active' : ''}`} 
                            onClick={() => setCurrentPage(1)}
                        >
                            Personal Details
                        </span>
                    </div>
                    {currentPage === 0 ? (
                        <>
                            <div className='sub-title'>
                        <h4>Basic Information</h4>
                        <div className='person-or-company'>
                            <div
                                className={`poc person${!contactInformation.is_company ? ' active' : ''}`}
                                onClick={() => setContactInformation({ ...contactInformation, is_company: false })}
                                title='Is the contact a person?'
                            >
                                <Person />
                            </div>
                            <div
                                className={`poc company${contactInformation.is_company ? ' active' : ''}`}
                                onClick={() => setContactInformation({ ...contactInformation, is_company: true })}
                                title='Is the contact a company?'
                            >
                                <Building />
                            </div>
                        </div>
                        <div className="contact-type">
                            <Dropdown
                                options={contactTypes}
                                placeholder="Select Contact Type"
                                onChange={(index) => {
                                    const selectedType = contactTypes[index] || '';
                                    setContactInformation({ ...contactInformation, contact_type: selectedType });
                                }}
                                value={contactTypes.indexOf(contactInformation.contact_type)}
                            />
                        </div>
                        <Checkbox
                            label="Is this an opportunity?"
                            value={contactInformation.opportunity}
                            onChange={() => setContactInformation({ ...contactInformation, opportunity: !contactInformation.opportunity })}
                        />
                    </div>
                    <div className='contact-picture-container'>
                        <div className="contact-picture form-box" onClick={() => document.getElementById('profilePictureInput').click()}>
                            {profilePicture ? (
                                <img className='contact-picture-listed' src={profilePicturePreviewUrl || ''} alt="Contact" />
                            ) : (
                                <Person />
                            )}
                            <input
                                type="file"
                                id="profilePictureInput"
                                accept="image/*"
                                style={{ display: 'none' }}
                                onChange={handleProfilePictureChange}
                            />
                        </div>
                        {profilePicture && <button className='action quinary'>
                            Remove Picture
                        </button>}
                    </div>
                    <div className='contact-name-container'>
                        {!contactInformation.is_company && (
                            <div className='contact-name-info'>
                                <div className="form-group nm fname name-fg">
                                    <label className='subtext' htmlFor="first_name">First Name<span className='required'>*</span></label>
                                    <input type="text" id="first_name" autoFocus value={contactInformation.first_name} onChange={(e) => setContactInformation({ ...contactInformation, first_name: e.target.value })} />
                                </div>
                                <div className="form-group nm mname name-fg">
                                    <label className='subtext' htmlFor="middle_name">Middle Name</label>
                                    <input type="text" id="middle_name" value={contactInformation.middle_name} onChange={(e) => setContactInformation({ ...contactInformation, middle_name: e.target.value })} />
                                </div>
                                <div className="form-group nm lname name-fg">
                                    <label className='subtext' htmlFor="last_name">Last Name<span className='required'>*</span></label>
                                    <input type="text" id="last_name" value={contactInformation.last_name} onChange={(e) => setContactInformation({ ...contactInformation, last_name: e.target.value })} />
                                </div>
                                <div className="form-group nm nickname">
                                    <label className='subtext' htmlFor="nickname">Nickname</label>
                                    <input type="text" id="nickname" value={contactInformation.nickname} onChange={(e) => setContactInformation({ ...contactInformation, nickname: e.target.value })} />
                                </div>
                                <div className="form-group nm prefix name-fg">
                                    <label className='subtext' htmlFor="prefix">Prefix</label>
                                    <input type="text" id="prefix" value={contactInformation.prefix} onChange={(e) => setContactInformation({ ...contactInformation, prefix: e.target.value })} />
                                </div>
                                <div className="form-group nm suffix name-fg">
                                    <label className='subtext' htmlFor="suffix">Suffix</label>
                                    <input type="text" id="suffix" value={contactInformation.suffix} onChange={(e) => setContactInformation({ ...contactInformation, suffix: e.target.value })} />
                                </div>
                            </div>
                        )}
                        <div className='divider horizontal'></div>
                        <div className="form-group nm cname">
                            <label className='subtext' htmlFor="company_name">Company Name</label>
                            <input type="text" id="company_name" value={contactInformation.company_name} onChange={(e) => setContactInformation({ ...contactInformation, company_name: e.target.value })} />
                        </div>
                        <div className="form-group nm jtitle">
                            <label className='subtext' htmlFor="job_title">Job Title</label>
                            <input type="text" id="job_title" value={contactInformation.job_title} onChange={(e
                            ) => setContactInformation({ ...contactInformation, job_title: e.target.value })} />
                        </div>
                        <div className="form-group nm dname">
                            <label className='subtext' htmlFor="department">Department</label>
                            <input type="text" id="department" value={contactInformation.department} onChange={(e) => setContactInformation({ ...contactInformation, department: e.target.value })} />
                        </div>
                        <div className="form-group nm dob">
                            <label className='subtext' htmlFor="date_of_birth">{!contactInformation.is_company ? "Date of Birth" : "Creation Date"}{formatAge(contactInformation.date_of_birth)}</label>
                            <input type="date" id="date_of_birth" value={contactInformation.date_of_birth} onChange={(e) => setContactInformation({ ...contactInformation, date_of_birth: e.target.value })} />
                        </div>
                        {!contactInformation.is_company && (
                            <div className="form-group nm dod">
                                <label className='subtext' htmlFor="date_of_death">Date of Death</label>
                                <input type="date" id="date_of_death" value={contactInformation.date_of_death} onChange={(e) => setContactInformation({ ...contactInformation, date_of_death: e.target.value })} />
                            </div>
                        )}
                        <div className='form-group nm pref-contact'>
                            <label className='subtext' htmlFor='preferred_contact_method'>Preferred Contact Method</label>
                            <select className='default-select' id='preferred_contact_method'>
                                <option value='' disabled>Select...</option>
                                <option value='email'>Email</option>
                                <option value='phone'>Phone</option>
                                <option value='mail'>Mail</option>
                            </select>
                        </div>
                    </div>
                    <div className='sub-title'>
                        <h4>Contact Information</h4>
                    </div>
                    <div className='contact-information'>
                        <div className='detail-container'>
                            {phones.map((phone, index) => (
                                <div key={`phone-${index}`} className='phone-number-container'>
                                    <div className='form-group nm phone-type'>
                                        <label className='subtext' htmlFor={`phone-type-${index}`}>Phone Type</label>
                                        <select className='default-select' value={phone.type} onChange={(e) => {
                                            const newPhones = [...phones];
                                            newPhones[index].type = e.target.value;
                                            setPhones(newPhones);
                                        }}>
                                            <option value="mobile">Mobile</option>
                                            <option value="home">Home</option>
                                            <option value="work">Work</option>
                                            <option value="workmobile">Mobile (Work)</option>
                                            <option value="fax">Fax</option>
                                            <option value="other">Other</option>
                                        </select>
                                    </div>
                                    <div className='form-group nm phone-input'>
                                        <label className='subtext' htmlFor={`phone-number-${index}`}>Number</label>
                                        <input type="tel" id={`phone-number-${index}`} value={phone.number} onChange={(e) => {
                                            const newPhones = [...phones];
                                            newPhones[index].number = e.target.value;
                                            setPhones(newPhones);
                                        }} />
                                    </div>
                                    <button 
                                        type="button" 
                                        className={`form-box ${phone?.note?.length > 0 ? 'desc' : 'alt'}`}
                                        title='Add/Edit phone notes'
                                        style={phone.note ? { color:  'var(--background-color)' } : { color: 'inherit', backgroundColor: 'var(--border-color)' }}
                                        onClick={() => setShowNoteFor(showNoteFor === `phone-${index}` ? null : `phone-${index}`)}
                                    >
                                        <StickyNote size={14} />
                                    </button>
                                    {showNoteFor === `phone-${index}` && (
                                        <div className='contact-note-editor'>
                                            <textarea 
                                                ref={noteTextareaRef}
                                                placeholder="Phone notes..."
                                                value={phone.note || ""} 
                                                onChange={(e) => {
                                                    const newPhones = [...phones];
                                                    newPhones[index].note = e.target.value;
                                                    setPhones(newPhones);
                                                }} 
                                                onBlur={() => setShowNoteFor(null)}
                                                rows="3"
                                            />
                                        </div>
                                    )}
                                    {index > 0 ? (
                                        <button className='form-box alt' title='Remove this phone number' onClick={() => {
                                            const newPhones = [...phones];
                                            newPhones.splice(index, 1);
                                            setPhones(newPhones);
                                        }}><Minus size={14} /></button>
                                    ) : (
                                        <div className='form-box alt' title='Add another phone number' onClick={addPhone}><Plus size={14} /></div>
                                    )}
                                </div>
                            ))}
                        </div>
                        <div className='divider horizontal'></div>
                        <div className='detail-container'>
                            {emails.map((email, index) => (
                                <div key={`email-${index}`} className='email-container'>
                                    <div className='form-group nm email-type'>
                                        <label className='subtext' htmlFor={`email-type-${index}`}>Email Type</label>
                                        <select className='default-select' id={`email-type-${index}`} value={email.type} onChange={(e) => {
                                            const newEmails = [...emails];
                                            newEmails[index].type = e.target.value;
                                            setEmails(newEmails);
                                        }}>
                                            <option value="personal">Personal</option>
                                            <option value="work">Work</option>
                                            <option value="other">Other</option>
                                        </select>
                                    </div>
                                    <div className='form-group nm email-input'>
                                        <label className='subtext' htmlFor={`email-address-${index}`}>Email</label>
                                        <input type="email" id={`email-address-${index}`} value={email.email} onChange={(e) => {
                                            const newEmails = [...emails];
                                            newEmails[index].email = e.target.value;
                                            setEmails(newEmails);
                                        }} />
                                    </div>
                                    <button 
                                        type="button" 
                                        className={`form-box ${email?.note?.length > 0 ? 'desc' : 'alt'}`}
                                        title='Add/Edit email notes'
                                        style={email.note ? { color:  'var(--background-color)' } : { color: 'inherit', backgroundColor: 'var(--border-color)' }}
                                        onClick={() => setShowNoteFor(showNoteFor === `email-${index}` ? null : `email-${index}`)}
                                    >
                                        <StickyNote size={14} />
                                    </button>
                                    {showNoteFor === `email-${index}` && (
                                        <div className='contact-note-editor'>
                                            <textarea 
                                                ref={noteTextareaRef}
                                                placeholder="Email notes..."
                                                value={email.note || ""} 
                                                onChange={(e) => {
                                                    const newEmails = [...emails];
                                                    newEmails[index].note = e.target.value;
                                                    setEmails(newEmails);
                                                }} 
                                                onBlur={() => setShowNoteFor(null)}
                                                rows="3"
                                            />
                                        </div>
                                    )}
                                    {index > 0 ? (
                                        <button className='form-box alt' title='Remove this email' onClick={() => {
                                            const newEmails = [...emails];
                                            newEmails.splice(index, 1);
                                            setEmails(newEmails);
                                        }}><Minus size={14} /></button>
                                    ) : (
                                        <div className='form-box alt' title='Add another email' onClick={addEmail}><Plus size={14} /></div>
                                    )}
                                </div>
                            ))}
                        </div>
                        <div className='divider horizontal'></div>
                        {addresses.map((address, index) => (
                            <div key={index} className='address'>
                                <div className='address-heading'>
                                    <span className="subheading">
                                        {formatFullAddress(address) || `Address ${index + 1}`}
                                    </span>
                                    {formatFullAddress(address) && (
                                        <span
                                            type="button"
                                            className='copy-paste'
                                            onClick={() => copyAddressToClipboard(address, index)}
                                            title="Copy full address to clipboard"
                                        >
                                            {copiedAddressIndex === index ? (
                                                <CheckCheck size={12} />
                                            ) : (
                                                <Copy size={12} />
                                            )}
                                        </span>
                                    )}
                                </div>
                                <div className='form-group nm address-detail main-data'>
                                    <label className='subtext' htmlFor="address-type">Type</label>
                                    <select className='default-select' value={address.type} name='address-type' onChange={(e) => {
                                        const updated = [...addresses];
                                        updated[index].type = e.target.value;
                                        setAddresses(updated);
                                    }}>
                                        <option value="home">Home</option>
                                        <option value="work">Work</option>
                                        <option value="other">Other</option>
                                    </select>
                                </div>
                                <div className='form-group nm address-detail main-data'>
                                    <label className='subtext'>Address Line 1</label>
                                    <input type="text" value={address.line1} onChange={(e) => {
                                        const updated = [...addresses];
                                        updated[index].line1 = e.target.value;
                                        setAddresses(updated);
                                    }} />
                                </div>
                                <div className='form-group nm address-detail main-data'>
                                    <label className='subtext'>Address Line 2</label>
                                    <input type="text" value={address.line2} onChange={(e) => {
                                        const updated = [...addresses];
                                        updated[index].line2 = e.target.value;
                                        setAddresses(updated);
                                    }} />
                                </div>
                                <div className='form-group nm address-detail main-data'>
                                    <label className='subtext'>City</label>
                                    <input type="text" value={address.city} onChange={(e) => {
                                        const updated = [...addresses];
                                        updated[index].city = e.target.value;
                                        setAddresses(updated);
                                    }} />
                                </div>
                                <div className='form-group nm address-detail'>
                                    <label className='subtext'>State</label>
                                    <input type="text" value={address.state} onChange={(e) => {
                                        const updated = [...addresses];
                                        updated[index].state = e.target.value;
                                        setAddresses(updated);
                                    }} />
                                </div>
                                <div className='form-group nm address-detail postal'>
                                    <label className='subtext'>Postal Code</label>
                                    <input type="text" value={address.postal_code} onChange={(e) => {
                                        const updated = [...addresses];
                                        updated[index].postal_code = e.target.value;
                                        setAddresses(updated);
                                    }} />
                                </div>
                                <div className='detail-box-container'>
                                    <button 
                                        type="button" 
                                        className={`form-box ${address?.note?.length > 0 ? 'desc' : 'alt'}`}
                                        title='Add/Edit address notes'
                                        style={ address.note ? { color:  'var(--background-color)' } : { color: 'inherit', backgroundColor: 'var(--border-color)' }}
                                        onClick={() => setShowNoteFor(showNoteFor === `address-${index}` ? null : `address-${index}`)}
                                    >
                                        <StickyNote size={14} />
                                    </button>
                                    {index > 0 ? (
                                        <button type="button" className='form-box alt' title='Remove this address' onClick={() => {
                                            const updated = [...addresses];
                                            updated.splice(index, 1);
                                            setAddresses(updated);
                                        }}><Minus size={14} /></button>
                                    ) : (
                                        <button type="button" className='form-box alt' title='Add another address' onClick={addAddress}><Plus size={14} /></button>
                                    )}
                                </div>
                                {showNoteFor === `address-${index}` && (
                                    <div className='form-group nm full contact-note-editor'>
                                        <label className='subtext' htmlFor={`create-contact-address-note-${index}`}>Address notes</label>
                                        <textarea
                                            id={`create-contact-address-note-${index}`}
                                            ref={noteTextareaRef}
                                            placeholder="Address notes..."
                                            value={address.note || ""}
                                            onChange={(e) => {
                                                const updated = [...addresses];
                                                updated[index].note = e.target.value;
                                                setAddresses(updated);
                                            }}
                                            onBlur={() => setShowNoteFor(null)}
                                            rows="3"
                                        />
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                        </>
                    ) : (
                        <>
                            <div className='sub-title'>
                                <h4>Personal Information</h4>
                            </div>
                            <div className='contact-name-container'>
                                {!contactInformation.is_company && (
                                    <>
                                        <div className='contact-name-info'>
                                            <div className="form-group nm gender">
                                                <label className='subtext' htmlFor="gender">Gender</label>
                                                <select className='default-select' id="gender" value={contactInformation.gender} onChange={(e) => setContactInformation({ ...contactInformation, gender: e.target.value })}>
                                                    <option value="" disabled>Select Gender</option>
                                                    <option value="unknown">Unknown</option>
                                                    <option value="male">Male</option>
                                                    <option value="female">Female</option>
                                                    <option value="other">Gender Diverse</option>
                                                </select>
                                            </div>
                                            <div className="form-group nm pronoun">
                                                <label className='subtext' htmlFor="pronouns">Pronouns</label>
                                                <select className='default-select' id="pronouns" value={contactInformation.pronouns} onChange={(e) => setContactInformation({ ...contactInformation, pronouns: e.target.value })}>
                                                    <option value="" disabled>Select Pronouns</option>
                                                    <option value="he">He/Him</option>
                                                    <option value="she">She/Her</option>
                                                    <option value="they">They/Them</option>
                                                </select>
                                            </div>
                                            <div className="form-group nm language">
                                                <label className='subtext' htmlFor="language">Language Spoken</label>
                                                <input type="text" id="language" value={contactInformation.language} onChange={(e) => setContactInformation({ ...contactInformation, language: e.target.value })} />
                                            </div>
                                            <div className="form-group nm emergency-contact">
                                                <label className='subtext' htmlFor="nickname">Emergency Contact</label>
                                                <Contact 
                                                    selectedContact={contactInformation.emergencyContact}
                                                    setSelectedContact={(contact) => setContactInformation({ ...contactInformation, emergencyContact: contact?.id || contact })}
                                                    onCreateNewContact={() => {/* Handle nested contact creation if needed */}}
                                                />
                                            </div>
                                            <div className="form-group nm social-media">
                                                <label className='subtext' htmlFor="prefix">Social Media Pages</label>
                                                <div className='social-media-links'>
                                                    {Array.isArray(contactInformation?.socialMedia) && contactInformation.socialMedia.length > 0 ? (
                                                        contactInformation.socialMedia.map((link, index) => (
                                                            <div key={index} className='social-media-input-container'>
                                                                <Text
                                                                    type='link'
                                                                    value={link}
                                                                    onChange={(value) => {
                                                                        const newSocialMedia = [...contactInformation.socialMedia];
                                                                        newSocialMedia[index] = value;
                                                                        setContactInformation({
                                                                            ...contactInformation,
                                                                            socialMedia: newSocialMedia,
                                                                        });
                                                                    }}
                                                                />
                                                                {index > 0 ? (
                                                                    <button 
                                                                        className='form-box alt' 
                                                                        title='Remove this social media link' 
                                                                        onClick={() => {
                                                                            const newSocialMedia = [...contactInformation.socialMedia];
                                                                            newSocialMedia.splice(index, 1);
                                                                            setContactInformation({
                                                                                ...contactInformation,
                                                                                socialMedia: newSocialMedia,
                                                                            });
                                                                        }}
                                                                    >
                                                                        <Minus size={14} />
                                                                    </button>
                                                                ) : (
                                                                    <div 
                                                                        className='form-box alt' 
                                                                        title='Add another social media link' 
                                                                        onClick={() => {
                                                                            setContactInformation({
                                                                                ...contactInformation,
                                                                                socialMedia: [...contactInformation.socialMedia, ''],
                                                                            });
                                                                        }}
                                                                    >
                                                                        <Plus size={14} />
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ))
                                                    ) : (
                                                        <div className='social-media-input-container'>
                                                            <Text
                                                                type='link'
                                                                value=""
                                                                onChange={(value) => {
                                                                    setContactInformation({
                                                                        ...contactInformation,
                                                                        socialMedia: [value],
                                                                    });
                                                                }}
                                                            />
                                                            <div 
                                                                className='form-box alt' 
                                                                title='Add another social media link' 
                                                                onClick={() => {
                                                                    setContactInformation({
                                                                        ...contactInformation,
                                                                        socialMedia: ['', ''],
                                                                    });
                                                                }}
                                                            >
                                                                <Plus size={14} />
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="form-group nm ssn name-fg">
                                                <label className='subtext' htmlFor="ssn">Social Security Number</label>
                                                <input type="text" id="ssn" value={contactInformation.ssn} onChange={(e) => setContactInformation({ ...contactInformation, ssn: e.target.value })} />
                                            </div>
                                            <div className="form-group nm dl name-fg">
                                                <label className='subtext' htmlFor="dl">Driver's License Number</label>
                                                <input type="text" id="dl" value={contactInformation.dl} onChange={(e) => setContactInformation({ ...contactInformation, dl: e.target.value })} />
                                            </div>
                                            <div className="form-group nm dlp name-fg">
                                                <label className='subtext' htmlFor="ssn">Driver's License/ID Card (Front and Back)</label>
                                                <MultiFile
                                                    id="dlp"
                                                    value={contactInformation.dlp}
                                                    onChange={(files) => setContactInformation({ ...contactInformation, dlp: files })}
                                                />
                                            </div>
                                            <div className="form-group nm education name-fg">
                                                <label className='subtext' htmlFor="education">Education</label>
                                                <textarea id="education" value={contactInformation.education} onChange={(e) => setContactInformation({ ...contactInformation, education: e.target.value })} />
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                            <div className='divider horizontal'></div>
                            <ContactFieldPresets
                                presets={contactPresets}
                                setPresets={setContactPresets}
                                setFieldMap={setPresetFieldMap}
                            />
                            <div className='divider horizontal'></div>
                            <div className='sub-title'>
                                <h4>Family Information</h4>
                            </div>
                            <div className='family-information'>
                                <div className='form-group nm additional-notes'>
                                    <label className='subtext' htmlFor="additional_notes">Additional Notes</label>
                                    <textarea id="additional_notes" value={contactInformation.additional_notes} onChange={(e) => setContactInformation({ ...contactInformation, additional_notes: e.target.value })} />
                                </div>
                            </div>
                        </>
                    )}
                </form>
            </div>
        </Modal>
    );
};
